import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { MapPin } from "lucide-react";
import { VENUE_COORDS } from "@/lib/wedding-data";

const toRad = (d) => (d * Math.PI) / 180;
const haversineKm = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.sqrt(a)));
};

export default function LocationPrompt() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("viyush-loc-asked")) return;
    const t = setTimeout(() => setShow(true), 2500);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    localStorage.setItem("viyush-loc-asked", "1");
    setShow(false);
  };

  const allow = () => {
    localStorage.setItem("viyush-loc-asked", "1");
    setShow(false);
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const d = haversineKm(
          pos.coords.latitude,
          pos.coords.longitude,
          VENUE_COORDS.lat,
          VENUE_COORDS.lng
        );
        localStorage.setItem("viyush-distance", String(d));
        window.dispatchEvent(new CustomEvent("viyush-location"));
        toast.success(
          d >= 500
            ? "That's quite a journey - we've added flight and train suggestions for you."
            : "Thank you - we've tailored travel advice for you."
        );
        window.__lenis?.scrollTo("#journey", { offset: -72 });
      },
      () => toast.info("No problem - travel tips are waiting in the Logistics section."),
      { timeout: 10000 }
    );
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.4 }}
          data-testid="location-prompt"
          className="fixed bottom-6 inset-x-0 z-50 flex justify-center px-6"
        >
          <div className="gold-frame w-full max-w-lg rounded-2xl bg-[#FDFBF7] border border-[#D4AF37]/40 p-6 md:p-8 flex items-start gap-5">
            <div className="h-11 w-11 shrink-0 rounded-full bg-[#5D171E] text-[#E1C699] flex items-center justify-center">
              <MapPin className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <p className="font-serif text-xl text-[#1A1A1A] tracking-tight">
                Travelling in for the wedding?
              </p>
              <p className="mt-1 text-sm text-[#1A1A1A]/60">
                Share your location once and we'll suggest whether to drive,
                take a train, or book a flight.
              </p>
              <div className="mt-4 flex gap-3">
                <button
                  onClick={allow}
                  data-testid="location-prompt-allow"
                  className="rounded-full bg-[#5D171E] text-[#FDFBF7] text-xs uppercase tracking-[0.15em] px-6 py-3 hover:bg-[#471016] transition-colors duration-300"
                >
                  Share Location
                </button>
                <button
                  onClick={dismiss}
                  data-testid="location-prompt-dismiss"
                  className="rounded-full border border-[#D4AF37]/50 text-[#5D171E] text-xs uppercase tracking-[0.15em] px-6 py-3 hover:border-[#5D171E] transition-colors duration-300"
                >
                  Not Now
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
